import { Box, Button, Dialog, HStack, Icon, Portal, Spinner, Text, VStack } from '@chakra-ui/react'
import axios from 'axios'
import { toaster } from 'commons/design-system/components/Toaster'
import { BgColors, TextColors } from 'commons/design-system/tokens'
import { renderPdfToImage } from 'commons/utils/fileUtils'
import { getShareUrl } from 'commons/utils/shareUrlHelpers'
import { QRCodeSVG } from 'qrcode.react'
import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MdClose, MdDownload, MdPrint, MdQrCode2 } from 'react-icons/md'

type QrCodeButtonProps = {
	slug: string
	title?: string
}

const isIosSafari = () => {
	const userAgent = window.navigator.userAgent
	const isIos = /iPad|iPhone|iPod/.test(userAgent)
	const isSafari = /Safari/.test(userAgent) && !/CriOS|FxiOS|EdgiOS/.test(userAgent)
	return isIos && isSafari
}

export const QrCodeButton = ({ slug, title }: QrCodeButtonProps) => {
	const { t } = useTranslation()
	const [isPdfLoading, setIsPdfLoading] = useState(false)
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [pdfBlob, setPdfBlob] = useState<Blob | null>(null)
	const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null)
	const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null)
	const pdfBlobUrlRef = useRef<string | null>(null)
	const generationIdRef = useRef(0)

	const cleanup = useCallback(() => {
		if (pdfBlobUrlRef.current) {
			URL.revokeObjectURL(pdfBlobUrlRef.current)
			pdfBlobUrlRef.current = null
		}
		setPdfBlob(null)
		setPdfBlobUrl(null)
		setPreviewImageUrl(null)
	}, [])

	const generateQrCodePdf = useCallback(async () => {
		const generationId = ++generationIdRef.current
		setIsPdfLoading(true)
		cleanup()
		try {
			const response = await axios.post(
				`${process.env.REACT_APP_GATEWAY_BASE_URL}lydia-app/v1/collect/${slug}/qr_template/_generate`,
				null,
				{ responseType: 'arraybuffer' }
			)
			const blob = new Blob([response.data], { type: 'application/pdf' })
			const blobUrl = URL.createObjectURL(blob)
			const imageUrl = await renderPdfToImage(new Uint8Array(response.data))

			if (generationId !== generationIdRef.current) {
				URL.revokeObjectURL(blobUrl)
				return
			}

			pdfBlobUrlRef.current = blobUrl
			setPdfBlob(blob)
			setPdfBlobUrl(blobUrl)
			setPreviewImageUrl(imageUrl)
		} catch {
			if (generationId === generationIdRef.current) {
				toaster.create({
					title: t('money_pot.qr_code_error_title'),
					description: t('money_pot.qr_code_error_description'),
					type: 'error',
					duration: 5000,
					closable: true,
					action: {
						label: t('money_pot.qr_code_error_retry'),
						onClick: () => generateQrCodePdf()
					}
				})
			}
		} finally {
			if (generationId === generationIdRef.current) {
				setIsPdfLoading(false)
			}
		}
	}, [slug, cleanup, t])

	const handleClick = () => {
		setIsModalOpen(true)
		generateQrCodePdf()
	}

	const handleClose = () => {
		generationIdRef.current += 1
		setIsModalOpen(false)
		cleanup()
	}

	const handleDownload = async () => {
		if (!pdfBlob) return

		const filename = `qr-code-${slug}.pdf`
		const file = new File([pdfBlob], filename, {
			type: 'application/pdf'
		})

		if (isIosSafari() && navigator.canShare?.({ files: [file] })) {
			await navigator.share({
				files: [file],
				title: filename
			})
			return
		}

		const url = URL.createObjectURL(pdfBlob)
		const link = document.createElement('a')
		link.href = url
		link.download = filename
		document.body.appendChild(link)
		link.click()
		link.remove()

		setTimeout(() => URL.revokeObjectURL(url), 1000)
	}

	const handlePrint = () => {
		if (!pdfBlobUrl) return
		const printWindow = window.open(pdfBlobUrl)
		if (printWindow) {
			printWindow.addEventListener('load', () => {
				printWindow.print()
			})
		}
	}

	const qrCodeUrl = getShareUrl()

	return (
		<>
			<Button
				bg='white'
				color={TextColors.PRIMARY}
				onClick={handleClick}
				borderRadius='full'
				ml={2}
				aria-label={t('money_pot.qr_code_modal_title')}
			>
				<Icon as={MdQrCode2} boxSize='20px' />
			</Button>

			<Dialog.Root open={isModalOpen} onOpenChange={handleClose} size='lg' placement='center'>
				<Portal>
					<Dialog.Backdrop />
					<Dialog.Positioner>
						<Dialog.Content borderRadius='xl' maxW={{ base: '95vw', md: '670px' }}>
							<Dialog.CloseTrigger asChild>
								<Button variant='ghost' size='sm' onClick={handleClose}>
									<Icon as={MdClose} boxSize='20px' />
								</Button>
							</Dialog.CloseTrigger>
							<Dialog.Body p={{ base: 6, md: 8 }}>
								<VStack gap={6} align='stretch'>
									<Text
										fontSize='xl'
										fontWeight='bold'
										color={TextColors.PRIMARY}
										fontFamily='Degular'
										textAlign='center'
									>
										{t('money_pot.qr_code_modal_title')}
									</Text>
									{title && (
										<Text
											as='h2'
											fontSize={{ base: '2xl', md: '3xl' }}
											fontWeight='bold'
											color={TextColors.PRIMARY}
											textAlign='center'
											lineHeight='short'
										>
											{title}
										</Text>
									)}
									<Box display='flex' justifyContent='center'>
										<QRCodeSVG
											value={qrCodeUrl}
											size={260}
											level='H'
											role='img'
											title={t('money_pot.qr_code_modal_title')}
											aria-label={t('money_pot.qr_code_modal_title')}
											style={{ maxWidth: '100%', height: 'auto' }}
										/>
									</Box>
									<Box borderTop='1px dashed' borderColor={BgColors.Z2} />
									<HStack
										align='center'
										gap={{ base: 4, md: 6 }}
										p={{ base: 4, md: 5 }}
										bg={BgColors.Z4}
										borderRadius='2xl'
										flexDirection={{ base: 'column', md: 'row' }}
									>
										<Box
											w={{ base: '150px', md: '180px' }}
											minW={{ md: '180px' }}
											minH='90px'
											display='flex'
											alignItems='center'
											justifyContent='center'
											bg={BgColors.Z5}
											borderRadius='sm'
											boxShadow='sm'
											overflow='hidden'
										>
											{previewImageUrl ? (
												<img
													src={previewImageUrl}
													alt={t('money_pot.qr_code_preview_title')}
													style={{ display: 'block', width: '100%', height: 'auto' }}
												/>
											) : (
												<Spinner size='sm' color={TextColors.PRIMARY} />
											)}
										</Box>
										<VStack align={{ base: 'center', md: 'start' }} gap={3} flex='1'>
											<Text
												fontSize='sm'
												fontWeight='medium'
												textAlign={{ base: 'center', md: 'left' }}
											>
												{t('money_pot.qr_code_printable_description')}
											</Text>
											<HStack gap={3} flexWrap='wrap' justify={{ base: 'center', md: 'start' }}>
												<Button
													onClick={handleDownload}
													bg={BgColors.OPPOSITE_Z3}
													color={TextColors.OPPOSITE_PRIMARY}
													borderRadius='md'
													disabled={!pdfBlob || isPdfLoading}
												>
													<Icon as={MdDownload} boxSize='18px' />
													{t('money_pot.qr_code_download')}
												</Button>
												<Button
													onClick={handlePrint}
													variant='outline'
													borderColor={BgColors.Z1}
													color={TextColors.PRIMARY}
													borderRadius='md'
													disabled={!pdfBlobUrl || isPdfLoading}
												>
													<Icon as={MdPrint} boxSize='18px' />
													{t('money_pot.qr_code_print')}
												</Button>
											</HStack>
										</VStack>
									</HStack>
								</VStack>
							</Dialog.Body>
						</Dialog.Content>
					</Dialog.Positioner>
				</Portal>
			</Dialog.Root>
		</>
	)
}
